import React from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth, usePermissions } from '../../hooks';
import { UserRole } from '../../types';
import { hasRole } from '../../utils';
import { CircularProgress, Box } from '@mui/material';

interface ProtectedRouteProps {
  children?: React.ReactNode;
  requiredRole?: UserRole;
  requiredPermission?: string | string[];
  requireAllPermissions?: boolean;
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermission,
  requireAllPermissions = false,
  redirectTo = '/auth/login',
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const { hasPermission, hasAnyPermission, hasAllPermissions, isAdmin } = usePermissions();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role-based access if required role is specified
  if (requiredRole && !hasRole(user.role, requiredRole)) {
    return <Navigate to="/staff" replace />;
  }

  // Check granular permission access if specified
  if (requiredPermission && !isAdmin) {
    let isAllowed = false;
    if (Array.isArray(requiredPermission)) {
      if (requireAllPermissions) {
        isAllowed = hasAllPermissions(requiredPermission);
      } else {
        isAllowed = hasAnyPermission(requiredPermission);
      }
    } else {
      isAllowed = hasPermission(requiredPermission);
    }

    if (!isAllowed) {
      return <Navigate to="/staff" replace />;
    }
  }

  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;